// Mini in-memory store met pub/sub. Geen browser-storage (zie BUILD.md §5).
// State leeft in dit module en is over de hele app gedeeld via getStore().

const listeners = new Set()

const state = {
  // Multi-regio
  regions: null,       // Array van region-objecten uit regions.json
  region: null,        // Actieve region-object { id, label, file, ... }

  // Geladen seed + afgeleide indexen (zie data.js)
  data: null,
  indexes: null,

  // Routing (geparsed uit window.location.hash)
  route: { tab: 'map', kind: 'map', params: {} },

  // Kaartfilters
  filters: {
    tiers: null,         // Set<string>; null = nog niet geïnitialiseerd
    stars: 0,            // minimumsterren (0 = alle)
    eras: null,          // Set<string>; null = alle
    categories: null,    // Set<string>; null = alle
    onlyFavorites: false,// true = alleen favorieten op de kaart tonen
    showHikes: false,    // true = wandelroutes tonen op de kaart
  },

  // Speler-state (alleen runtime; persistente kopie staat server-side)
  player: {
    id: null,
    name: null,
  },

  // LLM-resultaten: nodeId → section[] (gevuld na een Maak-volledige-entry-call)
  generatedSections: Object.create(null),

  // Compendium-cache: "title@lang" → { summary, url, license, ... }
  wikipediaCache: Object.create(null),

  // Game-state: gespiegeld van /api/state
  game: {
    bingo: Object.create(null),       // bingoId → spelernaam
    quizDone: new Set(),              // quizId
    scores: Object.create(null),      // spelernaam → punten
    favorites: Object.create(null),   // nodeId → true
    discoveries: [],                  // AI-ontdekte plekken op de kaart
  },
}

export function getStore() {
  return state
}

export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function notify(reason) {
  for (const fn of listeners) {
    try { fn(reason) } catch (e) { console.error('store-listener fout', e) }
  }
}

// Convenience-mutaties met automatische notify
export function setRegions(regions) {
  state.regions = regions
}

export function setRegion(region) {
  state.region = region
  notify('region')
}

export function setRoute(route) {
  state.route = route
  notify('route')
}

export function setData(data, indexes) {
  state.data = data
  state.indexes = indexes
  // Filters resetten bij region-wissel
  const visibleTiers = new Set(
    data.meta.tiers.filter(t => t.defaultVisible !== false).map(t => t.id)
  )
  state.filters = {
    tiers: visibleTiers,
    stars: 0,
    eras: null,
    categories: null,
    onlyFavorites: false,
    showHikes: false,
  }
  // Game-state resetten
  state.generatedSections = Object.create(null)
  state.wikipediaCache = Object.create(null)
  notify('data')
}

export function setFilter(key, value) {
  state.filters[key] = value
  notify('filters')
}

export function setPlayer(p) {
  Object.assign(state.player, p)
  notify('player')
}

export function setGeneratedSections(nodeId, sections) {
  state.generatedSections[nodeId] = sections
  notify('generated')
}

export function setWikipediaEntry(key, entry) {
  state.wikipediaCache[key] = entry
  notify('wikipedia')
}

export function setGameState(partial) {
  if (partial.bingo) state.game.bingo = { ...state.game.bingo, ...partial.bingo }
  if (partial.quizDone) state.game.quizDone = new Set(partial.quizDone)
  if (partial.scores) state.game.scores = { ...state.game.scores, ...partial.scores }
  if (partial.favorites) state.game.favorites = { ...state.game.favorites, ...partial.favorites }
  if (partial.discoveries) state.game.discoveries = partial.discoveries
  notify('game')
}

export function toggleFavorite(nodeId) {
  const cur = !!state.game.favorites[nodeId]
  const next = { ...state.game.favorites }
  if (cur) delete next[nodeId]
  else next[nodeId] = true
  state.game.favorites = next
  notify('favorites')
  return !cur  // nieuwe status
}

// ---------- Feature-gating ----------
export function isFeatureOn(featureKey) {
  if (!state.data) return false
  const feat = state.data.meta?.features
  if (!feat) return true
  return feat[featureKey] !== false
}

export function hasCollection(collectionKey) {
  if (!state.data) return false
  const coll = state.data[collectionKey]
  return Array.isArray(coll) && coll.length > 0
}

export function isFeatureAvailable(featureKey, collectionKey) {
  if (!collectionKey) return isFeatureOn(featureKey)
  return isFeatureOn(featureKey) && hasCollection(collectionKey)
}
